import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  CALL_APP_URL,
  CALL_SPLIT_MAX_RATIO,
  CALL_SPLIT_MIN_RATIO,
  normalizeCallEmbedUrl,
} from "../../lib/board/callAppUrl";
import type { BoardCallSessionStatus } from "../../hooks/useBoardCallSession";
import { useBoardCallSplit } from "../../hooks/useBoardCallSplit";
import { useCallEmbedInRoom } from "../../hooks/useCallEmbedInRoom";
import { useCallLiveIndicator } from "../../hooks/useCallLiveIndicator";
import { useCallMiniIframeScale } from "../../hooks/useCallMiniIframeScale";

type Props = {
  children: ReactNode;
  /** URL комнаты звонилки; если не задан — корень CALL_APP_URL. */
  callEmbedUrl?: string | null;
  /** Автооткрытие панели (например ?callOpen=1 после старта урока). */
  initialOpen?: boolean;
  /** Доступна ли звонилка; если нет — кнопка остаётся, но с замочком. */
  allowCall?: boolean;
  /** Как идут дела со ссылкой на комнату (см. useBoardCallSession). */
  callStatus?: BoardCallSessionStatus;
  /** «Попробовать ещё раз», если ссылку на комнату получить не удалось. */
  onRetryCall?: () => void;
};

/**
 * Сколько ждём, прежде чем поверить «не в звонке» после того, как человек
 * в нём был: на переподключении звонилка может коротко сказать «нет».
 */
const LEFT_CALL_CONFIRM_MS = 3_000;

const CALL_LOCKED_HINT = "Звонок доступен на тарифе Pro";
const CALL_LOCKED_HINT_MS = 3500;

function miniHeaderMode(width: number): "normal" | "compact" | "tiny" {
  if (width < 250) return "tiny";
  if (width < 320) return "compact";
  return "normal";
}

function resolveIframeSrc(callEmbedUrl?: string | null): string {
  return normalizeCallEmbedUrl(callEmbedUrl?.trim() || CALL_APP_URL);
}

export function BoardCallSplitLayout({
  children,
  callEmbedUrl,
  initialOpen = false,
  allowCall = true,
  callStatus = "loading",
  onRetryCall,
}: Props) {
  const splitRef = useRef<HTMLDivElement>(null);
  const callShellRef = useRef<HTMLElement>(null);
  const {
    callView,
    callActive,
    open,
    toggleOpen,
    minimizeCall,
    hideCall,
    miniWindowRect,
    onMiniWindowDragPointerDown,
    onMiniWindowResizePointerDown,
    callRatio,
    desktopCallRatio,
    mobileCallRatio,
    onHandlePointerDown,
    boardRatio,
    setCallView,
    isMobileLayout,
    snapMiniWindowToContent,
  } = useBoardCallSplit(splitRef, callShellRef);

  const isMinimized = callView === "minimized";
  const supportsZoom = useMemo(
    () => typeof CSS !== "undefined" && CSS.supports("zoom", "1"),
    []
  );
  const { scale, refWidth, refHeight } = useCallMiniIframeScale(isMinimized, callShellRef);
  const headerMode = isMinimized ? miniHeaderMode(miniWindowRect.width) : "normal";

  /** Не менять src после первой загрузки комнаты — иначе iframe перезагрузится. */
  const callIframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeSrc, setIframeSrc] = useState(() => resolveIframeSrc(callEmbedUrl));
  useEffect(() => {
    const next = resolveIframeSrc(callEmbedUrl);
    setIframeSrc((prev) => {
      if (next.includes("/room/")) return next;
      if (prev.includes("/room/")) return prev;
      return next;
    });
  }, [callEmbedUrl]);

  const {
    embedInCall,
    callEnded,
    onIframeLoad: onCallIframeLoad,
    resetEmbedState,
    markCallEngaged,
  } = useCallEmbedInRoom(
    callActive && iframeSrc.includes("/room/"),
    iframeSrc,
    callIframeRef
  );
  const callLive = useCallLiveIndicator(
    callView,
    embedInCall,
    callEnded,
    resetEmbedState,
    markCallEngaged
  );

  const hasRoom = iframeSrc.includes("/room/");
  const openTabHref = hasRoom ? iframeSrc : CALL_APP_URL;

  /**
   * Человек нажал в звонке «Выйти» — звонилка уходит на свою главную
   * страницу. Раньше она так и оставалась в панели: открыли «Звонок» снова —
   * а там главная, где легко создать новую комнату и разминуться с
   * учеником. Теперь панель закрываем своей заглушкой «Вернуться в звонок».
   */
  const [iframeKey, setIframeKey] = useState(0);
  const [wasInCall, setWasInCall] = useState(false);
  const [leftCall, setLeftCall] = useState(false);
  useEffect(() => {
    if (embedInCall === true) {
      setWasInCall(true);
      setLeftCall(false);
      return;
    }
    if (!wasInCall || embedInCall !== false) return;
    const t = window.setTimeout(() => setLeftCall(true), LEFT_CALL_CONFIRM_MS);
    return () => window.clearTimeout(t);
  }, [embedInCall, wasInCall]);
  const rejoinCall = () => {
    setWasInCall(false);
    setLeftCall(false);
    resetEmbedState();
    setIframeKey((k) => k + 1);
  };

  /** Кнопка с замочком: по нажатию коротко объясняем, почему звонок закрыт. */
  const [lockedHintOpen, setLockedHintOpen] = useState(false);
  const lockedHintTimerRef = useRef(0);
  const showLockedHint = () => {
    setLockedHintOpen(true);
    window.clearTimeout(lockedHintTimerRef.current);
    lockedHintTimerRef.current = window.setTimeout(
      () => setLockedHintOpen(false),
      CALL_LOCKED_HINT_MS
    );
  };
  useEffect(() => () => window.clearTimeout(lockedHintTimerRef.current), []);

  useEffect(() => {
    if (!allowCall) {
      setCallView("closed");
      return;
    }
    if (initialOpen) setCallView("docked");
  }, [allowCall, initialOpen, setCallView]);

  useEffect(() => {
    if (!isMinimized) return;
    const id = requestAnimationFrame(() => {
      snapMiniWindowToContent();
    });
    return () => cancelAnimationFrame(id);
  }, [isMinimized, headerMode, snapMiniWindowToContent]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });
    return () => cancelAnimationFrame(id);
  }, [open, callRatio, callView, miniWindowRect.width, miniWindowRect.height]);

  useEffect(() => {
    const el = splitRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      window.dispatchEvent(new Event("resize"));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const miniWindowStyle = isMinimized
    ? ({
        left: `${miniWindowRect.x}px`,
        top: `${miniWindowRect.y}px`,
        width: `${miniWindowRect.width}px`,
        height: `${miniWindowRect.height}px`,
      } as CSSProperties)
    : undefined;

  const frameHostStyle: CSSProperties | undefined = isMinimized
    ? {
        width: `${refWidth * scale}px`,
        height: `${refHeight * scale}px`,
      }
    : undefined;

  const frameScaleStyle: CSSProperties | undefined = isMinimized
    ? supportsZoom
      ? {
          width: `${refWidth}px`,
          height: `${refHeight}px`,
          zoom: scale,
        }
      : {
          width: `${refWidth}px`,
          height: `${refHeight}px`,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }
    : undefined;

  const iframeStyle: CSSProperties | undefined = isMinimized
    ? { width: `${refWidth}px`, height: `${refHeight}px` }
    : undefined;

  const expandLabel =
    headerMode === "tiny" ? "▲" : headerMode === "compact" ? "Разв." : "Развернуть";
  const collapseLabel =
    headerMode === "tiny" ? "−" : headerMode === "compact" ? "Сверн." : "Свернуть";
  const hideLabel = headerMode === "tiny" ? "⎋" : "Скрыть";
  const tabLabel = headerMode === "tiny" ? "↗" : headerMode === "compact" ? "Вкл." : "Вкладка";

  return (
    <div
      ref={splitRef}
      className={`wb-board-split${open ? " is-call-open" : ""}${
        isMinimized ? " is-call-minimized" : ""
      }${callView === "hidden" ? " is-call-hidden" : ""}`}
      style={
        open
          ? ({
              "--wb-call-ratio": String(desktopCallRatio),
              "--wb-call-ratio-mobile": String(mobileCallRatio),
            } as CSSProperties)
          : undefined
      }
    >
      <div className="wb-board-split__board">{children}</div>

      {allowCall && open ? (
        <div
          className="wb-board-split__handle"
          role="separator"
          aria-orientation={isMobileLayout ? "horizontal" : "vertical"}
          aria-label={isMobileLayout ? "Высота панели звонилки" : "Ширина панели звонилки"}
          aria-valuemin={Math.round(CALL_SPLIT_MIN_RATIO * 100)}
          aria-valuemax={Math.round(CALL_SPLIT_MAX_RATIO * 100)}
          aria-valuenow={Math.round(callRatio * 100)}
          onPointerDown={onHandlePointerDown}
        >
          <span className="wb-board-split__handle-grip" aria-hidden />
        </div>
      ) : null}

      {allowCall && callActive ? (
        <aside
          ref={callShellRef}
          className={`wb-board-call-shell${
            callView === "docked"
              ? " is-docked"
              : isMinimized
                ? ` is-minimized is-mini-${headerMode}`
                : " is-hidden"
          }`}
          style={miniWindowStyle}
          aria-label="Звонилка"
          aria-hidden={callView === "hidden"}
        >
          <header
            className="wb-board-call-shell__header"
            onPointerDown={isMinimized ? onMiniWindowDragPointerDown : undefined}
          >
            {headerMode === "tiny" ? null : (
              <span className="wb-board-call-shell__title">
                {isMinimized ? "Звонок идёт" : "Звонилка"}
              </span>
            )}
            <div className="wb-board-call-shell__actions">
              {isMinimized ? (
                <button
                  type="button"
                  className="wb-btn wb-btn--ghost wb-btn--sm wb-board-call-shell__action"
                  onClick={toggleOpen}
                  title="Развернуть звонилку"
                  aria-label="Развернуть звонилку"
                >
                  {expandLabel}
                </button>
              ) : callView === "docked" ? (
                <button
                  type="button"
                  className="wb-btn wb-btn--ghost wb-btn--sm wb-board-call-shell__action"
                  onClick={minimizeCall}
                  title="Свернуть в маленькое окно — звонок продолжится"
                >
                  {collapseLabel}
                </button>
              ) : null}
              <button
                type="button"
                className="wb-btn wb-btn--ghost wb-btn--sm wb-board-call-shell__action"
                onClick={hideCall}
                title="Скрыть панель — звонок продолжится в фоне"
                aria-label="Скрыть"
              >
                {hideLabel}
              </button>
              <a
                className="wb-btn wb-btn--ghost wb-btn--sm wb-board-split__open-tab wb-board-call-shell__action"
                href={openTabHref}
                target="_blank"
                rel="noopener noreferrer"
                title="Открыть звонилку во вкладке"
              >
                {tabLabel}
              </a>
            </div>
          </header>
          <div className={`wb-board-call-shell__body${isMinimized ? " is-scaled" : ""}`}>
            <div
              className="wb-board-call-shell__frame-host"
              style={isMinimized ? frameHostStyle : undefined}
            >
              <div
                className="wb-board-call-shell__frame-scale"
                style={isMinimized ? frameScaleStyle : undefined}
              >
                {/* Без ссылки на комнату звонилку не грузим вовсе: её главная
                    страница уводила людей в разные комнаты. */}
                {hasRoom ? (
                <iframe
                  key={iframeKey}
                  ref={callIframeRef}
                  className="wb-board-call-shell__frame"
                  // Для обращений: в каком виде была панель, когда человека
                  // перестали слышать. Спрятанная панель на айпаде глушит захват.
                  data-call-view={callView}
                  style={isMinimized ? iframeStyle : undefined}
                  src={iframeSrc}
                  title="Bezpart звонилка"
                  // speaker-selection — выбор наушников/динамика внутри панели,
                  // screen-wake-lock — телефон не гасит экран во время урока.
                  // Без них браузер молча запрещает это именно во фрейме.
                  allow="camera *; microphone *; display-capture *; autoplay *; clipboard-write *; speaker-selection *; screen-wake-lock *"
                  referrerPolicy="strict-origin-when-cross-origin"
                  onLoad={onCallIframeLoad}
                />
                ) : null}
              </div>
            </div>
            {!hasRoom ? (
              <div className="wb-board-call-shell__notice" role="status" aria-live="polite">
                {callStatus === "unavailable" ? (
                  <p>Звонок на этой доске недоступен.</p>
                ) : callStatus === "failed" ? (
                  <>
                    <p>Не получается подключиться к звонку. Проверьте интернет — доска продолжит пробовать сама.</p>
                    {onRetryCall ? (
                      <button type="button" className="wb-btn wb-btn--sm" onClick={onRetryCall}>
                        Попробовать ещё раз
                      </button>
                    ) : null}
                  </>
                ) : (
                  <p>Подключаем звонок…</p>
                )}
              </div>
            ) : leftCall ? (
              <div className="wb-board-call-shell__notice" role="status">
                <p>Вы вышли из звонка.</p>
                <button type="button" className="wb-btn wb-btn--sm" onClick={rejoinCall}>
                  Вернуться в звонок
                </button>
              </div>
            ) : null}
          </div>
          {isMinimized ? (
            <div
              className="wb-board-call-shell__resize"
              role="presentation"
              aria-hidden
              onPointerDown={onMiniWindowResizePointerDown}
            />
          ) : null}
        </aside>
      ) : null}

      {!open ? (
        <button
          type="button"
          className={`wb-board-call-toggle${allowCall && callLive ? " is-call-live" : ""}${
            allowCall ? "" : " is-locked"
          }`}
          onClick={allowCall ? toggleOpen : showLockedHint}
          title={
            !allowCall ? CALL_LOCKED_HINT : callLive ? "Развернуть звонилку" : "Открыть звонилку"
          }
          aria-label={
            !allowCall
              ? `Звонок недоступен. ${CALL_LOCKED_HINT}`
              : callLive
                ? "Развернуть звонилку"
                : "Открыть звонилку"
          }
        >
          <svg
            className="wb-board-call-toggle__icon"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          <span className="wb-board-call-toggle__label">Звонок</span>
          {allowCall && callLive ? (
            <span className="wb-board-call-toggle__live" aria-hidden />
          ) : null}
          {!allowCall ? (
            <svg
              className="wb-board-call-toggle__lock"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
          ) : null}
          {!allowCall && lockedHintOpen ? (
            <span className="wb-board-call-toggle__hint" role="status">
              {CALL_LOCKED_HINT}
            </span>
          ) : null}
        </button>
      ) : null}

      {allowCall && open ? (
        <span className="wb-board-split__ratio-hint" aria-hidden>
          Доска {Math.round(boardRatio * 100)}% · Звонок {Math.round(callRatio * 100)}%
        </span>
      ) : null}
    </div>
  );
}
